import { useEffect, useMemo, useState, type ReactNode } from 'react'

import {
  MINE,
  narrowingOf,
  turnRepository,
  useFoodRepositories,
  useRepositoriesOff,
} from '~/lib/food-repositories'
import { useFocusOnMount } from '~/lib/focus-on-mount'

import { FoodMark } from '~/components/food-mark'
import { FoodName } from '~/components/food-name'
import { CheckIcon, MoreIcon, PlusIcon, SearchIcon } from '~/components/icons'
import { FilterChip } from '~/components/ui/filter-chip'
import { SidewaysRow } from '~/components/ui/sideways-row'
import { Skeleton, SkeletonBlock } from '~/components/skeleton'
import { useApi, useDataState } from '~/data/data-context'
import { mealDeck } from '~/data/food-emoji'
import {
  askingToLog,
  placedFavourites,
  placedMeals,
  placedOwn,
  placedRecents,
  usePlacing,
  useFavouriteFoods,
  useFavouriteMeals,
  useMyFoods,
  useMyMeals,
  useRecentFoods,
} from '~/data/food-store'
import { todayKey } from '~/data/weighins'
import type { Asking } from '@gymcloud/nutrition/suggestions'

import {
  amountSaid,
  foodKey,
  formatCalories,
  macroLine,
  mealOfLog,
  mealOpensTo,
  nowTime,
  referenceSaid,
  sourceSaid,
  type Amount,
  type Food,
  type FoundFood,
  type Meal,
  type MealLog,
} from '~/data/nutrition'

/** How long typing rests before the library is asked. */
const SEARCH_REST_MS = 250

/**
 * What a list is placed for where the page did not say — logging onto today, at the moment the
 * list opened. Read once, as it opens, and held: a list is placed for the moment it was opened
 * at and stands in that order until it is opened again, so neither a keystroke in the field nor
 * the clock crossing into the evening rearranges it under the hand.
 */
function useAskingHere(asked: Asking | undefined): Asking {
  const [here] = useState(() => askingToLog(todayKey(), nowTime()))
  return asked ?? here
}

/**
 * One food as a list offers it: its name, its brand where it has one, its calories per its
 * reference amount, and where it comes from. Pressing it is whatever the list is for —
 * picking it, opening it — and a picked one wears a tick.
 */
export function FoodRow({
  food,
  source,
  picked = false,
  detail,
  onPress,
  onAbout,
  trailing,
}: {
  food: Food
  /** Where it comes from, where a list mixes sources. */
  source?: string | undefined
  picked?: boolean
  /** A second line beyond the calories — the amount it was last logged in. */
  detail?: string | undefined
  onPress: () => void
  /** Opening the food itself: what it is, and what can be done with it. */
  onAbout?: (() => void) | undefined
  /** A control at the far end, beside the row rather than inside its press. */
  trailing?: ReactNode
}) {
  return (
    <li className="flex items-center gap-2">
      <button
        type="button"
        data-food-row
        aria-pressed={picked}
        onClick={onPress}
        className={`flex min-w-0 flex-1 items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition-colors hover:bg-hover ${
          picked ? 'bg-accent/10' : ''
        }`}
      >
        {picked ? (
          <span
            aria-hidden
            className="grid size-9 shrink-0 place-items-center rounded-full bg-accent text-accent-ink"
          >
            <CheckIcon className="size-5" />
          </span>
        ) : (
          <FoodMark food={food} />
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate">
            <FoodName food={food} />
          </span>
          <span className="block truncate text-sm text-ink-muted">
            {formatCalories(food.nutrients.calories)} per {referenceSaid(food)}
            {detail !== undefined && ` · ${detail}`}
            {source !== undefined && ` · ${source}`}
            {food.recipe !== null && ' · recipe'}
          </span>
        </span>
      </button>
      {trailing}
      {onAbout !== undefined && (
        <button
          type="button"
          aria-label={`About ${food.name}`}
          className="icon-btn shrink-0"
          onClick={onAbout}
        >
          <MoreIcon aria-hidden className="size-6" />
        </button>
      )}
    </li>
  )
}

/**
 * A meal as a list offers it, per the spec's Meals: it says it is a meal, and names what its
 * members come to together where a food names its calories. Pressing it is opening the meal's
 * page, wherever it stands.
 */
export function MealRow({
  meal,
  onPress,
  kind = 'meal',
}: {
  meal: Meal
  onPress: () => void
  /** What it is, where it is not quite a meal — something logged without being kept. */
  kind?: string
}) {
  return (
    <li className="flex items-center gap-2">
      <button
        type="button"
        data-food-row
        onClick={onPress}
        className="flex min-w-0 flex-1 items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition-colors hover:bg-hover"
      >
        <FoodMark deck={mealDeck(meal)} />
        <span className="min-w-0 flex-1">
          <span className="block truncate">
            <bdi>{meal.name}</bdi>
          </span>
          <span className="block truncate text-sm text-ink-muted">
            {macroLine(mealOpensTo(meal))} · {kind}
          </span>
        </span>
      </button>
    </li>
  )
}

/** The account's meals, as a list to open — the "Mine" tab beside the foods. */
export function MyMealsList({
  onMeal,
  alone = false,
  asking,
}: {
  onMeal: (meal: Meal) => void
  /** Standing as a page of its own, which names it, rather than as a section under a heading. */
  alone?: boolean
  /** What the list is placed for; logging onto today, at the moment it opened, unless said. */
  asking?: Asking | undefined
}) {
  const held = useMyMeals()
  const heldLogs = useDataState().mealLogs
  const placing = usePlacing(useAskingHere(asking))
  /*
   * The logs and the log place the meals, so nothing is drawn until they have arrived —
   * settled data before settled UI.
   */
  const meals =
    held === null || heldLogs === null || placing === null
      ? null
      : placedMeals(held.meals, heldLogs, placing)

  if (meals === null) {
    return <ListSkeleton label="Loading your meals" />
  }
  if (meals.length === 0) {
    return alone ? (
      <p className="px-3 py-6 text-center text-sm text-ink-muted">No meals of your own yet.</p>
    ) : null
  }
  return (
    <>
      {!alone && (
        <h3 className="px-3 text-xs font-medium tracking-wider text-ink-muted uppercase">Meals</h3>
      )}
      <ul aria-label="Meals" className="space-y-0.5">
        {meals.map((meal) => (
          <MealRow
            key={meal.id}
            meal={meal}
            onPress={() => {
              onMeal(meal)
            }}
          />
        ))}
      </ul>
    </>
  )
}

/**
 * The foods searched by name, across the library and the account's own, each saying which it
 * is from. Before anything is typed the recent foods stand here
 * instead, each with the amount it was last logged in, which picking it opens on. A search
 * that cannot be answered says a connection is needed rather than that nothing was found.
 */
export function FoodSearch({
  picked,
  onPick,
  onMeal,
  onLog,
  onAbout,
  onCreate,
  asking,
  typed,
}: {
  /** The keys of the foods already picked, which wear a tick. */
  picked: ReadonlySet<string>
  /**
   * Picking a food, with the amount it was last logged in where the list knows it — a recent
   * food carries its own, and picking it opens on that rather than on a hundred grams.
   */
  onPick: (food: Food, last?: Amount | null, mine?: boolean) => void
  /**
   * Opening a meal's page — pressing a meal opens it rather than picking it. Left out, the
   * list offers no meals at all: a meal cannot stand inside a recipe or another meal.
   */
  onMeal?: ((meal: Meal) => void) | undefined
  /**
   * Opening something logged without being kept — the AI's answer, a shared meal — which
   * stands among the foods logged before as itself, on the page as it was.
   */
  onLog?: ((log: MealLog) => void) | undefined
  /** Opening a food to see what it is and what can be done with it. */
  onAbout?: ((food: Food, mine: boolean) => void) | undefined
  /** Creating a food from what was typed, where nothing found fits. */
  onCreate?: ((name: string) => void) | undefined
  /**
   * What the lists are placed for — `/specs/features/food-order.spec.md`. A page logging onto
   * a day says that day and the time it is logging at; a form says itself, holding what it
   * already has. Left out, it is logging onto today at the moment this opened.
   */
  asking?: Asking | undefined
  /**
   * What has been typed, where the field asking for it is not this list's own — the header's
   * search on web, which is the field the food log searches from. Left out, the list carries a
   * field of its own, which is what every page that opens the picker gives it.
   */
  typed?: string | undefined
}) {
  const api = useApi()
  const [ownQuery, setOwnQuery] = useState('')
  const query = typed ?? ownQuery
  /*
   * Where what is found may come from — the nutrition spec, Logging an entry. Until the packs
   * are known, a search with anything turned off waits rather than answering unnarrowed and
   * then taking results away again.
   */
  const repositories = useFoodRepositories()
  const off = useRepositoriesOff()
  const narrowingKnown = repositories !== null || off.size === 0
  const from = repositories === null ? null : narrowingOf(repositories, off)
  const fromKey = from?.join(',') ?? ''
  const mineOn = !off.has(MINE)
  const [results, setResults] = useState<FoundFood[] | null>(null)
  const [failed, setFailed] = useState(false)
  /** The library gave no answer, which is not the library holding nothing. */
  const [libraryUnavailable, setLibraryUnavailable] = useState(false)
  const offersMeals = onMeal !== undefined
  const field = useFocusOnMount<HTMLInputElement>()

  // All of these are the account's own, so they are read off what this device holds rather
  // than fetched a list at a time — which is what puts a food saved elsewhere in the picker.
  const favourites = useFavouriteFoods()
  const recent = useRecentFoods()
  const heldMeals = useMyMeals()
  /** The account's meals, where the list offers them; settled empty where it does not. */
  const meals = offersMeals ? (heldMeals === null ? null : [...heldMeals.meals]) : []
  const unkept: MealLog[] = offersMeals ? [...(heldMeals?.unsaved ?? [])] : []
  const heldMealFavourites = useFavouriteMeals()
  const mealFavourites = offersMeals ? heldMealFavourites : []
  /*
   * Every log the account holds, which is what a meal's parts of the day are read off — the
   * newest five of them, which is what every app holds of a meal. See [placedRecents].
   */
  const heldLogs = useDataState().mealLogs
  /* What places these lists — `/specs/features/food-order.spec.md`. See [useAskingHere]. */
  const placing = usePlacing(useAskingHere(asking))
  /*
   * The two lists offered before anything is typed, placed once for what they are placed off
   * rather than again on every render of the panel: a placing reads the whole log for its
   * habits, and the panel re-renders on every letter typed into the field over it.
   */
  const favouriteRows = useMemo(
    () =>
      favourites === null || heldLogs === null || placing === null
        ? []
        : placedFavourites(
            favourites,
            offersMeals ? (heldMealFavourites ?? []) : [],
            heldLogs,
            placing,
          ),
    [favourites, heldMealFavourites, offersMeals, heldLogs, placing],
  )
  const recentRows = useMemo(() => {
    if (recent === null || heldLogs === null || placing === null) {
      return []
    }
    const logged = offersMeals
      ? (heldMeals?.meals ?? [])
          .filter((meal) => meal.lastLoggedAt !== null)
          .sort((a, b) => (b.lastLoggedAt ?? 0) - (a.lastLoggedAt ?? 0))
      : []
    return placedRecents(
      recent,
      logged,
      offersMeals ? heldLogs : [],
      offersMeals ? (heldMeals?.unsaved ?? []) : [],
      placing,
    )
  }, [recent, heldMeals, offersMeals, heldLogs, placing])

  const needle = query.trim()
  /*
   * The last answer is the last question's, so it goes as the question changes — worked out
   * from the question rather than in the field's own hand, since the question can be asked
   * from a field this list does not own.
   */
  const [asked, setAsked] = useState(`${needle}\u0000${fromKey}`)
  if (asked !== `${needle}\u0000${fromKey}`) {
    setAsked(`${needle}\u0000${fromKey}`)
    setResults(null)
    setFailed(false)
    setLibraryUnavailable(false)
  }
  /** The meals logged before, standing among the foods logged before as themselves. */
  const loggedMeals = (meals ?? [])
    .filter((meal) => meal.lastLoggedAt !== null)
    .sort((a, b) => (b.lastLoggedAt ?? 0) - (a.lastLoggedAt ?? 0))
  /** The account's own stand among the results — a search finds a meal by name. */
  const matchingMeals =
    needle === '' || !mineOn
      ? []
      : (meals ?? []).filter((meal) => meal.name.toLowerCase().includes(needle.toLowerCase()))
  useEffect(() => {
    if (needle === '' || !narrowingKnown) {
      return
    }
    let alive = true
    const timer = window.setTimeout(() => {
      api.searchFoods(needle, fromKey === '' ? null : fromKey.split(',')).then(
        (got) => {
          if (alive) {
            setResults(got.foods)
            setLibraryUnavailable(got.libraryUnavailable)
            setFailed(false)
          }
        },
        () => {
          if (alive) {
            setResults([])
            setLibraryUnavailable(false)
            setFailed(true)
          }
        },
      )
    }, SEARCH_REST_MS)
    return () => {
      alive = false
      window.clearTimeout(timer)
    }
  }, [api, needle, fromKey, narrowingKnown])

  return (
    <div className="space-y-3">
      {typed === undefined && (
        <label className="flex h-12 items-center gap-3 rounded-full bg-surface-raised px-4">
          <SearchIcon className="size-6 shrink-0 text-ink-muted" />
          <input
            type="search"
            aria-label="Search foods"
            placeholder="Search foods"
            ref={field}
            value={ownQuery}
            onChange={(event) => {
              setOwnQuery(event.target.value)
            }}
            className="h-full w-full bg-transparent text-lg outline-none placeholder:text-ink-muted"
          />
        </label>
      )}

      {needle === '' &&
        recent !== null &&
        heldLogs !== null &&
        placing !== null &&
        ((favourites ?? []).length > 0 || (mealFavourites ?? []).length > 0) && (
          /* The favourites first — the nutrition spec, Favourites. One list, whatever kind of
             thing stands on it. */
          <>
            <h3 className="px-3 text-xs font-medium tracking-wider text-ink-muted uppercase">
              Favourites
            </h3>
            {/*
            A row that runs off the side rather than a list going down the panel: the
            favourites are few and are picked by their face, and a list of them takes the room
            the search's own answers want. The pointer moves it with the controls at its sides,
            the way the timeline's rows are moved — see `SidewaysRow`.
          */}
            <SidewaysRow as="ul" label="Favourites" className="gap-2 pb-1">
              {favouriteRows.map((row) =>
                row.kind === 'meal' ? (
                  <FavouriteCard
                    key={`meal-${row.meal.id}`}
                    mark={<FoodMark deck={mealDeck(row.meal)} size="size-11 text-xl" />}
                    name={row.meal.name}
                    detail="meal"
                    onPress={() => {
                      onMeal?.(row.meal)
                    }}
                  />
                ) : (
                  <FavouriteCard
                    key={foodKey(row.food)}
                    mark={<FoodMark food={row.food} size="size-11 text-xl" />}
                    name={row.food.name}
                    detail={formatCalories(row.food.nutrients.calories)}
                    onPress={() => {
                      onPick(row.food)
                    }}
                  />
                ),
              )}
            </SidewaysRow>
          </>
        )}

      {needle === '' ? (
        // Every list settles before anything is said about there being nothing: the favourites
        // stand above the recents, so an empty recents list is not an empty panel.
        recent === null ||
        favourites === null ||
        meals === null ||
        mealFavourites === null ||
        heldLogs === null ||
        placing === null ? (
          <ListSkeleton label="Loading recent foods" />
        ) : recent.length === 0 &&
          favourites.length === 0 &&
          mealFavourites.length === 0 &&
          unkept.length === 0 &&
          loggedMeals.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-ink-muted">
            Nothing logged yet. Search for a food to begin.
          </p>
        ) : (
          <>
            {/*
              What the reckoning expects next, greatest first.
            */}
            <h3 className="px-3 text-xs font-medium tracking-wider text-ink-muted uppercase">
              Suggested
            </h3>
            <ul className="space-y-0.5">
              {recentRows.map((row) =>
                row.kind === 'meal' ? (
                  <MealRow
                    key={`meal-${row.meal.id}`}
                    meal={row.meal}
                    onPress={() => {
                      onMeal?.(row.meal)
                    }}
                  />
                ) : row.kind === 'log' ? (
                  <MealRow
                    key={`log-${row.log.id}`}
                    meal={mealOfLog(row.log)}
                    kind="not kept"
                    onPress={() => {
                      onLog?.(row.log)
                    }}
                  />
                ) : (
                  <FoodRow
                    key={foodKey(row.each.food)}
                    food={row.each.food}
                    picked={picked.has(foodKey(row.each.food))}
                    detail={`last ${amountSaid(row.each.amount)}`}
                    onPress={() => {
                      onPick(row.each.food, row.each.amount)
                    }}
                    {...(onAbout === undefined
                      ? {}
                      : {
                          onAbout: () => {
                            onAbout(row.each.food, row.each.food.source === 'own')
                          },
                        })}
                  />
                ),
              )}
            </ul>
          </>
        )
      ) : (
        <>
          {/*
            What a search can be narrowed to, over what it found — a chip for the account's own
            and one per pack. They narrow what is typed and nothing else, so they stand only
            while something is.
          */}
          {repositories !== null && repositories.length > 1 && (
            <SidewaysRow label="Search in" role="group" className="gap-2 pb-1">
              {repositories.map((repository) => (
                <FilterChip
                  key={repository.id}
                  label={repository.label}
                  on={!off.has(repository.id)}
                  onToggle={() => {
                    turnRepository(repository.id, off.has(repository.id), repositories)
                  }}
                />
              ))}
            </SidewaysRow>
          )}
          {results === null ? (
            <ListSkeleton label="Searching" />
          ) : (
            <>
              {failed ? (
                <p className="px-3 py-2 text-sm text-ink-muted">
                  Searching needs a connection. Your own foods are still here under Mine.
                </p>
              ) : (
                <>
                  {libraryUnavailable && (
                    /* The library's part of the answer is missing, which is not the library
                   holding nothing — so what follows is never "nothing called that". */
                    <p className="px-3 py-2 text-sm text-ink-muted">
                      The library could not be searched just now. Your own foods are still here.
                    </p>
                  )}
                  {results.length === 0 && matchingMeals.length === 0 ? (
                    !libraryUnavailable && (
                      <p className="px-3 py-2 text-sm text-ink-muted">Nothing called that.</p>
                    )
                  ) : (
                    <ul className="space-y-0.5">
                      {/* The account's own stand first among the results, its meals with them. */}
                      {onMeal !== undefined &&
                        matchingMeals.map((meal) => (
                          <MealRow
                            key={`meal-${meal.id}`}
                            meal={meal}
                            onPress={() => {
                              onMeal(meal)
                            }}
                          />
                        ))}
                      {results.map((food) => (
                        <FoodRow
                          key={foodKey(food)}
                          food={food}
                          source={sourceSaid(food)}
                          picked={picked.has(foodKey(food))}
                          onPress={() => {
                            // Whose it is travels with it: a public food of somebody else is
                            // not the account's to change, whatever its source says.
                            onPick(food, null, food.mine)
                          }}
                          {...(onAbout === undefined
                            ? {}
                            : {
                                onAbout: () => {
                                  onAbout(food, food.mine)
                                },
                              })}
                        />
                      ))}
                    </ul>
                  )}
                </>
              )}
              {onCreate !== undefined && (
                <button
                  type="button"
                  data-food-row
                  className="flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left text-accent transition-colors hover:bg-hover"
                  onClick={() => {
                    onCreate(needle)
                  }}
                >
                  <PlusIcon aria-hidden className="size-6" />
                  Create “{needle}”
                </button>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}

/**
 * The account's own foods and recipes, as a list to pick from — or one kind of them alone,
 * where the page is about that kind.
 */
export function MyFoodsList({
  picked,
  onPick,
  onAbout,
  only,
  asking,
}: {
  picked: ReadonlySet<string>
  onPick: (food: Food, last?: Amount | null) => void
  /** Opening a food to see what it is and what can be done with it. */
  onAbout?: ((food: Food, mine: boolean) => void) | undefined
  /** Which kind alone, where the list is about one: plain foods, or recipes. */
  only?: 'foods' | 'recipes'
  /** What the list is placed for; logging onto today, at the moment it opened, unless said. */
  asking?: Asking | undefined
}) {
  const foods = useMyFoods()
  const placing = usePlacing(useAskingHere(asking))

  /*
   * The log places this list, so nothing is drawn until it has arrived: a list placed before
   * it came would stand every row in its never-logged order and then re-sort itself. Settled
   * data before settled UI.
   */
  if (foods === null || placing === null) {
    return <ListSkeleton label="Loading your foods" />
  }
  const shown = placedOwn(
    only === undefined
      ? foods
      : foods.filter((food) => (only === 'recipes' ? food.recipe !== null : food.recipe === null)),
    placing,
  )
  if (shown.length === 0) {
    return (
      <p className="px-3 py-6 text-center text-sm text-ink-muted">
        {only === 'recipes' ? 'No recipes yet.' : 'No foods of your own yet.'}
      </p>
    )
  }
  return (
    <ul className="space-y-0.5">
      {shown.map((food) => (
        <FoodRow
          key={foodKey(food)}
          food={food}
          picked={picked.has(foodKey(food))}
          onPress={() => {
            onPick(food)
          }}
          {...(onAbout === undefined
            ? {}
            : {
                onAbout: () => {
                  onAbout(food, true)
                },
              })}
        />
      ))}
    </ul>
  )
}

export function ListSkeleton({ label }: { label: string }) {
  return (
    <SkeletonBlock label={label} className="space-y-2 px-3">
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-12 w-full" />
    </SkeletonBlock>
  )
}

/**
 * One favourite in the row: its face, its name under it, and what it comes to under that. A
 * card rather than a row, because the row it stands in runs sideways — and small, since what
 * picks a favourite out is its face rather than anything written on it.
 */
function FavouriteCard({
  mark,
  name,
  detail,
  onPress,
}: {
  mark: ReactNode
  name: string
  /** The line under the name — what it comes to, or what kind of thing it is. */
  detail: string
  onPress: () => void
}) {
  return (
    <li className="shrink-0">
      <button
        type="button"
        data-food-row
        className="flex w-24 flex-col items-center gap-1 rounded-2xl px-2 py-2 text-center transition-colors hover:bg-hover"
        onClick={onPress}
      >
        {mark}
        <span className="w-full truncate text-xs">
          <bdi>{name}</bdi>
        </span>
        <span className="w-full truncate text-[0.6875rem] text-ink-muted">{detail}</span>
      </button>
    </li>
  )
}
